import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../hooks/useSeo";
import { ArrowRight, MapPin, Lightbulb, Users, Compass } from "lucide-react";

// Founders — bios written deliberately short, in the founders' own voice.
// Update freely; this is product copy, not boilerplate.
const FOUNDERS = [
  {
    initials: "SF",
    name: "Simon Fayers",
    roleNo: "Medgründer",
    roleEn: "Co-founder",
    quoteNo: "Hvordan kan vi gjøre hverdagen litt enklere?",
    quoteEn: "How can we make everyday life just a little easier?",
    bioNo: [
      "Simon er fysioterapeut i Asker med over 20 års erfaring i å hjelpe folk å løse hverdagslige utfordringer og forbedre livskvaliteten.",
      "Gjennom hele karrieren har han vært drevet av ett enkelt spørsmål: «Hvordan kan vi gjøre hverdagen litt enklere?» Pendli vokste fram av nettopp den måten å tenke på.",
      "Som mange pendlere har Simon kjent på hvor stressende og tidkrevende det kan være å finne parkering nær travle knutepunkt. Samtidig la han merke til at mange private innkjørsler og parkeringsplasser står tomme i løpet av dagen.",
      "I stedet for å anta at han hadde svaret, har Simon brukt tid på å snakke med pendlere, huseiere og lokalsamfunn for å forstå om Pendli faktisk løser et reelt problem før det lanseres.",
      "Ambisjonen er rett fram: skape en enkel og tillitvekkende tjeneste som hjelper pendlere å finne parkering, hjelper huseiere å utnytte ubrukt plass bedre, og gjør pendling litt enklere for alle.",
    ],
    bioEn: [
      "Simon is a physiotherapist based in Asker with more than 20 years of experience helping people solve everyday challenges and improve their quality of life.",
      "Throughout his career, he has always been driven by one simple question: \"How can we make everyday life just a little easier?\" Pendli grew from exactly that way of thinking.",
      "Like many commuters, Simon experienced how stressful and time-consuming it can be to find parking near busy transport hubs. At the same time, he noticed that many private driveways and parking spaces sit empty during the day.",
      "Rather than assuming he had the answer, Simon has spent time speaking with commuters, homeowners and local communities to understand whether Pendli solves a genuine problem before bringing it to market.",
      "His ambition is straightforward: create a simple, trustworthy service that helps commuters find parking, helps homeowners make better use of unused spaces, and makes commuting a little easier for everyone.",
    ],
  },
  {
    initials: "EH",
    name: "Erik Hedding-Reijrink",
    roleNo: "Medgründer",
    roleEn: "Co-founder",
    quoteNo: "Hvorfor skal noe stå ubrukt når noen andre trenger det?",
    quoteEn: "Why should something sit unused when someone else needs it?",
    bioNo: [
      "Erik er opprinnelig fra Nederland og har bodd i Norge i over 15 år. Til daglig jobber han med digital vekst og markedsføring i teknologibransjen, der han hjelper selskaper med å bygge tjenester folk faktisk finner, forstår og stoler på.",
      "Gjennom karrieren har han sett hvor stor forskjell det gjør når digitale løsninger er enkle å bruke – og hvor fort folk gir opp når de ikke er det. Den erfaringen tar han med seg inn i Pendli: en tjeneste for pendlere og huseiere må være så enkel og trygg at den nesten forklarer seg selv.",
      "Som innflytter har Erik også et blikk utenfra på norsk hverdagslogistikk. Han la tidlig merke til det samme som Simon: fulle pendlerparkeringer på den ene siden av gjerdet, tomme innkjørsler på den andre. Pendli er forsøket på å koble de to sammen.",
      "Ambisjonen hans er enkel: bygge en tjeneste der teknologien jobber i bakgrunnen, tilliten er på plass fra første klikk, og både pendlere og huseiere sitter igjen med en litt enklere hverdag.",
    ],
    bioEn: [
      "Erik is originally from the Netherlands and has lived in Norway for over 15 years. His day job is in digital growth and marketing in the tech industry, where he helps companies build services people actually find, understand and trust.",
      "Throughout his career he has seen what a difference it makes when digital solutions are easy to use – and how quickly people give up when they aren't. He brings that experience into Pendli: a service for commuters and hosts has to be so simple and safe that it almost explains itself.",
      "As someone who moved here, Erik also has an outsider's eye on everyday Norwegian logistics. He noticed early on the same thing Simon did: full commuter car parks on one side of the fence, empty driveways on the other. Pendli is the attempt to connect the two.",
      "His ambition is simple: build a service where the technology works in the background, trust is in place from the first click, and both commuters and hosts end up with a slightly easier everyday life.",
    ],
  },
];

const COPY = {
  no: {
    eyebrow: "Om Pendli",
    title: "Om Pendli",
    intro: "Pendli er en lokal idé. Vi forteller hvorfor vi bygger den, hvem som står bak — og hva som skjer akkurat nå.",
    s1Eyebrow: "Hvorfor",
    s1Title: "Hvorfor vi lager Pendli",
    s1: [
      "Pendli startet med et enkelt problem: Det kan være vanskelig, dyrt og stressende å finne parkering nær tog og kollektivtransport.",
      "Samtidig står mange private parkeringsplasser, innkjørsler og gårdsplasser tomme deler av dagen.",
      "Pendli skal gjøre det enklere å koble disse to behovene sammen — på en trygg, enkel og lokal måte.",
    ],
    s2Eyebrow: "Folkene",
    s2Title: "Hvem vi er",
    s2: [
      "Pendli utvikles av Simon og Erik, to lokale gründere som ønsker å gjøre pendlerhverdagen litt enklere i Asker og omegn.",
      "Vi bygger Pendli fordi vi tror det finnes en smartere måte å bruke parkeringsplassene som allerede finnes.",
    ],
    s3Eyebrow: "Status",
    s3Title: "Hva skjer nå?",
    s3: [
      "Vi forbereder en lokal lansering i Asker og samler nå interesse fra både pendlere og huseiere.",
      "Tilbakemeldingene vi får nå hjelper oss med å bygge en løsning som faktisk passer for folk som bor og pendler her.",
    ],
    s4Eyebrow: "Visjon",
    s4Title: "Vår visjon",
    s4: [
      "Pendli skal gjøre det enklere for pendlere å finne parkering, samtidig som huseiere kan tjene penger på plass som ellers står ubrukt.",
    ],
    badge: "Pre-launch · Asker stasjon",
    ctaTitle: "Har du spørsmål eller innspill?",
    ctaSub: "Vi svarer som regel innen 24 timer. Vi liker å høre fra folk.",
    ctaButton: "Ta kontakt med oss",
  },
  en: {
    eyebrow: "About Pendli",
    title: "About Pendli",
    intro: "Pendli is a local idea. Here's why we're building it, who's behind it — and what's happening right now.",
    s1Eyebrow: "Why",
    s1Title: "Why we're building Pendli",
    s1: [
      "Pendli started with a simple problem: finding parking near trains and public transit can be difficult, expensive, and stressful.",
      "At the same time, many private parking spots, driveways, and yards sit empty for parts of the day.",
      "Pendli aims to make it easier to connect those two needs — in a safe, simple, and local way.",
    ],
    s2Eyebrow: "The people",
    s2Title: "Who we are",
    s2: [
      "Pendli is being built by Simon and Erik, two local founders who want to make commuting a little easier in Asker and the surrounding area.",
      "We're building Pendli because we believe there's a smarter way to use the parking spots that already exist.",
    ],
    s3Eyebrow: "Status",
    s3Title: "What's happening now?",
    s3: [
      "We're preparing for a local launch in Asker and are currently gathering interest from both commuters and hosts.",
      "The feedback we're getting now helps us build a solution that actually fits people who live and commute here.",
    ],
    s4Eyebrow: "Vision",
    s4Title: "Our vision",
    s4: [
      "Pendli should make it easier for commuters to find parking, while allowing hosts to earn from space that would otherwise sit unused.",
    ],
    badge: "Pre-launch · Asker station",
    ctaTitle: "Questions or feedback?",
    ctaSub: "We usually reply within 24 hours. We like hearing from people.",
    ctaButton: "Get in touch",
  },
};

export default function OmOss() {
  const { t, lang } = useLang();
  const c = COPY[lang] || COPY.no;
  useSeo({ title: t.seo.om.title, description: t.seo.om.description, path: "/om-oss", lang });

  const sections = [
    { Icon: Lightbulb, eyebrow: c.s1Eyebrow, title: c.s1Title, paras: c.s1 },
    { Icon: Users, eyebrow: c.s2Eyebrow, title: c.s2Title, paras: c.s2, withFounders: true },
    { Icon: MapPin, eyebrow: c.s3Eyebrow, title: c.s3Title, paras: c.s3, withBadge: true },
    { Icon: Compass, eyebrow: c.s4Eyebrow, title: c.s4Title, paras: c.s4 },
  ];

  return (
    <main data-testid="om-oss-page" className="bg-ink text-cream pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 bay-lines opacity-30 pointer-events-none" />
        <div
          className="absolute -top-32 left-1/3 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(232,168,0,0.14) 0%, rgba(232,168,0,0) 70%)" }}
        />
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-12 pb-16 md:pt-20 md:pb-24 relative">
          <motion.span
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 border border-amber/40 px-3 py-1.5 text-xs font-bold tracking-widest uppercase text-amber"
          >
            <span className="w-1.5 h-1.5 bg-amber rounded-full" />
            {c.eyebrow}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-7 font-display text-5xl sm:text-7xl md:text-8xl font-extrabold leading-[1.02] tracking-tighter"
            style={{ letterSpacing: "-0.04em" }}
            data-testid="om-oss-headline"
          >
            {c.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 text-base md:text-xl text-cream/70 max-w-2xl leading-relaxed"
          >
            {c.intro}
          </motion.p>
        </div>
      </section>

      {/* SECTIONS */}
      <div className="max-w-4xl mx-auto px-6 md:px-10 pb-10 md:pb-16 space-y-20 md:space-y-28">
        {sections.map((s, idx) => (
          <motion.section
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-12 gap-8 md:gap-10"
            data-testid={`om-oss-section-${idx + 1}`}
          >
            <div className="md:col-span-4">
              <div className="w-12 h-12 bg-amber-deep border border-amber/30 flex items-center justify-center mb-5">
                <s.Icon size={20} className="text-amber" strokeWidth={1.6} />
              </div>
              <span className="text-xs font-bold tracking-widest uppercase text-amber/80">{s.eyebrow}</span>
              <h2
                className="mt-3 font-display text-3xl md:text-4xl font-extrabold leading-[1.05] tracking-tighter"
                style={{ letterSpacing: "-0.03em" }}
              >
                {s.title}
              </h2>
            </div>

            <div className="md:col-span-8 md:pt-2">
              <div className="space-y-5 text-base md:text-lg text-cream/80 leading-relaxed">
                {s.paras.map((p, i) => <p key={i}>{p}</p>)}
              </div>

              {s.withBadge && (
                <div className="mt-7 inline-flex items-center gap-2 border border-amber/40 px-3 py-1.5">
                  <span className="w-1.5 h-1.5 bg-amber rounded-full animate-pulse" />
                  <span className="text-xs font-bold tracking-widest uppercase text-amber">{c.badge}</span>
                </div>
              )}

              {s.withFounders && (
                <div className="mt-10 grid gap-6">
                  {FOUNDERS.map((f, i) => {
                    const bioParas = lang === "no" ? f.bioNo : f.bioEn;
                    const quote = lang === "no" ? f.quoteNo : f.quoteEn;
                    return (
                      <div
                        key={i}
                        className="relative bg-amber-deep/60 border border-amber/25 p-6 md:p-8 l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br"
                        data-testid={`founder-card-${f.name.toLowerCase().split(" ")[0]}`}
                      >
                        <div className="flex items-center gap-4 mb-5">
                          <div
                            className="w-14 h-14 bg-amber text-ink font-display font-extrabold flex items-center justify-center"
                            style={{ letterSpacing: "-0.03em", fontSize: f.initials.length > 1 ? "1.05rem" : "1.4rem" }}
                          >
                            {f.initials}
                          </div>
                          <div>
                            <p className="font-display text-xl font-bold tracking-tight">{f.name}</p>
                            <p className="text-xs font-bold tracking-widest uppercase text-amber/70">
                              {lang === "no" ? f.roleNo : f.roleEn}
                            </p>
                          </div>
                        </div>

                        {quote && (
                          <blockquote className="border-l-2 border-amber pl-4 mb-5 font-display text-base md:text-lg italic text-cream">
                            «{quote}»
                          </blockquote>
                        )}

                        <div className="space-y-3 text-sm md:text-base text-cream/75 leading-relaxed">
                          {bioParas.map((p, j) => <p key={j}>{p}</p>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.section>
        ))}
      </div>

      {/* CTA */}
      <section className="bg-cream text-ink py-16 md:py-24" data-testid="om-oss-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="border border-ink p-8 md:p-12 grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <h2 className="font-display text-3xl md:text-5xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
                {c.ctaTitle}
              </h2>
              <p className="mt-4 text-base md:text-lg text-ink/70 leading-relaxed">
                {c.ctaSub}
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/kontakt"
                data-testid="om-oss-cta-button"
                className="inline-flex items-center gap-2 h-13 px-6 py-3 bg-ink text-amber font-display font-bold text-base hover:bg-ink/85 transition-colors"
              >
                {c.ctaButton} <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
